import { lazy, use } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'

import { TRPCProvider } from './trpc'
import { DashboardProvider } from './context/DashboardContext'
import { AuthGate } from './components/AuthGate'
import { CampLoader } from './components/CampLoader'
import { GateShell } from './components/auth/GateShell'
import { DashboardSpeedInsights } from './components/DashboardSpeedInsights'
import { RoutePage } from './components/RoutePage'
import { LegacyRedirect, ProtectedRoute } from './components/routeGuards'
import { AdminPage } from './pages/AdminPage'
import { loadLoginPage, loadMagicLoginPage } from './pages/loaders'

const DashboardLayout = lazy(() => import('./components/DashboardLayout').then(module => ({ default: module.DashboardLayout })))

// The sign-in pages render in the same pass once main.tsx's early load has
// landed (pages/loaders.ts), and suspend into RoutePage's fallback until then.
function LoginRoute() {
  const { LoginPage } = use(loadLoginPage())
  return <LoginPage />
}

// MagicLoginPage removes the token from the URL as it mounts, as before: main.tsx starts its
// chunk with the session check, which AuthGate already waits for before any route mounts.
function MagicLoginRoute() {
  const { MagicLoginPage } = use(loadMagicLoginPage())
  return <MagicLoginPage />
}

const AgentPage = lazy(() => import('./pages/AgentPage').then(module => ({ default: module.AgentPage })))
const ClaudeCodePage = lazy(() => import('./pages/ClaudeCodePage').then(module => ({ default: module.ClaudeCodePage })))
const DocsPage = lazy(() => import('./pages/DocsPage').then(module => ({ default: module.DocsPage })))
const ChatPage = lazy(() => import('./pages/ChatPage').then(module => ({ default: module.ChatPage })))
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(module => ({ default: module.SettingsPage })))
const MusicPage = lazy(() => import('./pages/MusicPage').then(module => ({ default: module.MusicPage })))
const DocumentsPage = lazy(() => import('./pages/DocumentsPage').then(module => ({ default: module.DocumentsPage })))
const TodoPage = lazy(() => import('./pages/TodoPage').then(module => ({ default: module.TodoPage })))
const WhoopPage = lazy(() => import('./pages/WhoopPage').then(module => ({ default: module.WhoopPage })))
// Development-only viewer harness; `import.meta.env.DEV` is false in production builds, so the
// import is dropped there and the route is never registered.
const OfficeViewerHarness = import.meta.env.DEV
  ? lazy(() => import('./dev/OfficeViewerHarness').then(module => ({ default: module.OfficeViewerHarness })))
  : null

/**
 * `gate`: index.html painted the sign-in gate (main.tsx found it), so the
 * session check keeps that same gate on screen instead of the camp's loader.
 */
export function App({ gate = false }: { gate?: boolean }) {
  return (
    <TRPCProvider>
      <DashboardProvider>
        <BrowserRouter>
          <AuthGate pending={gate ? <GateShell /> : undefined}>
            <Routes>
              <Route path="/login" element={<RoutePage fallback={<GateShell />}><LoginRoute /></RoutePage>} />
              <Route path="/t/:tenant" element={<RoutePage fallback={<CampLoader />}><MagicLoginRoute /></RoutePage>} />
              <Route path="/setup-account" element={<Navigate to="/chat" replace />} />
              <Route path="/admin" element={<AdminPage />} />
              {OfficeViewerHarness ? (
                <Route path="/dev/office-viewer" element={<RoutePage><OfficeViewerHarness /></RoutePage>} />
              ) : null}
              <Route
                element={
                  <ProtectedRoute>
                    <RoutePage resetOnNavigation={false}><DashboardLayout /></RoutePage>
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/chat" replace />} />
                <Route path="/chat" element={<RoutePage><ChatPage /></RoutePage>} />
                <Route path="/todos" element={<RoutePage><TodoPage /></RoutePage>} />
                <Route path="/documents" element={<RoutePage><DocumentsPage /></RoutePage>} />
                <Route path="/music" element={<RoutePage><MusicPage /></RoutePage>} />
                <Route path="/whoop" element={<RoutePage><WhoopPage /></RoutePage>} />
                <Route path="/settings" element={<RoutePage><SettingsPage /></RoutePage>} />
                <Route path="/docs" element={<RoutePage><DocsPage /></RoutePage>} />
                <Route path="/docs/:slug" element={<RoutePage><DocsPage /></RoutePage>} />
                <Route path="/agent" element={<RoutePage><AgentPage /></RoutePage>} />
                <Route path="/claude-code" element={<RoutePage><ClaudeCodePage /></RoutePage>} />
                {/* A short link Tallgrass can point members at to paste their link code. */}
                <Route path="/tallgrass" element={<LegacyRedirect to="/settings?tab=tallgrass" />} />

                {/* Legacy redirects — the route IDs below were consolidated
                    into /settings or /music. Redirects preserve old deep
                    links (magic-link emails, bookmarks, pinned tabs) after
                    the nav shakeup. Tab deep links map through the
                    redirect's `?tab=` search param. */}
                <Route path="/dashboard" element={<LegacyRedirect to="/settings?tab=overview" />} />
                <Route path="/config" element={<LegacyRedirect to="/settings?tab=advanced" />} />
                <Route path="/persona" element={<LegacyRedirect to="/settings?tab=persona" />} />
                <Route path="/skills" element={<LegacyRedirect to="/settings?tab=skills" />} />
                <Route path="/billing" element={<LegacyRedirect to="/settings?tab=billing" />} />
                <Route path="/spotify" element={<LegacyRedirect to="/music?tab=spotify" />} />
                <Route path="/sonos" element={<LegacyRedirect to="/music?tab=sonos" />} />
                <Route path="/notifications" element={<Navigate to="/chat" replace />} />
                <Route path="/history" element={<Navigate to="/chat" replace />} />
              </Route>
              <Route path="*" element={<Navigate to="/chat" replace />} />
            </Routes>
          </AuthGate>
          <DashboardSpeedInsights />
          <Toaster
            position="top-right"
            containerStyle={{ top: 'calc(var(--spacing) * 16 + env(safe-area-inset-top))' }}
            toastOptions={{
              className: 'bl-toast',
              iconTheme: { primary: 'rgb(var(--color-primary))', secondary: 'rgb(var(--color-primary-contrast))' },
              error: { iconTheme: { primary: 'rgb(var(--color-error))', secondary: 'rgb(var(--color-text-inverse))' } },
            }}
          />
        </BrowserRouter>
      </DashboardProvider>
    </TRPCProvider>
  )
}
